import React, { useContext, useEffect, useReducer, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { LoadingOverlay, Loader } from "react-overlay-loader";
import { fetchSubFolders } from "src/actionCreators/folderActionCreators";
import { FolderColumnName, SortOrder } from "src/Core/Common/Enums";
import { IRetentionPeriod } from "src/Core/ViewModels/Company/Settings/RetentionSettings";
import { IFolderModel, rootFolder } from "src/Core/ViewModels/Folder/FolderModel";
import { getRetentionOptions } from "src/helper/HelperFunctions";
import { ApplicationState } from "src/store";
import { FolderType } from "src/Core/Common/Enums";
import { actionTypes } from "src/types/folderTypes";
import FolderToolbar from "../Message/Common/FolderToolBar";
import ArchiveFolderTable from "./ArchiveFolderTable";
import { ArchiveContext } from "./Archive";
import { AppNotifier } from "../Common/Notification/AppNotifier";
import { ToasterMessages } from "src/helper/ToasterMessages";

interface IMessageExplorerProps {
    handleFolderClick: (
        folder: IFolderModel,
        isTitleBreadcrumb: boolean,
        breadCrumbArrayArchiveTable: IFolderModel[]
    ) => void;
    retentionPeriods: IRetentionPeriod[];
    isRetentionLoading: boolean;
    isMessageLoading: boolean;
    isSearched?: boolean;
    currentFolder?: IFolderModel;
    setReload: (value: boolean) => void;
    goToParentFolder?: (value: boolean) => void;
    onHide?: () => void;
    selectedFolders?: IFolderModel[];
    setSelectedFolders?: (folders: IFolderModel[]) => void;
    goToFolder: (folderId: number, breadcrumbIndex: number, isTitleBreadcrumb: boolean) => void;
}

const MessageExplorer: React.FC<IMessageExplorerProps> = props => {
    const { selectedFolders, setSelectedFolders } = props;
    const [isMoveToFolderEnabled, setIsMoveToFolderEnabled] = useState(true);
    const [isToolbarReset, resetToolbar] = useState<boolean>(false);
    const currentFolderPath = useContext(ArchiveContext);
    const dispatch = useDispatch();

    const folderData = useSelector((appState: ApplicationState) => appState.folderState.foldersData.subFolders);
    const { mailboxUserData } = useSelector((appState: ApplicationState) => appState.commonState);
    const user = useSelector((state: ApplicationState) => state.userProfile);

    const [, forceUpdate] = useReducer(x => x + 1, 0);

    useEffect(() => {
        dispatch(
            fetchSubFolders(folderData ? folderData.parentFolderId : 0, FolderColumnName.FOLDERNAME, SortOrder.ASC)
        );
    }, []);

    useEffect(() => {
        handleFolderSelection([]);
    }, [mailboxUserData, props.isSearched]);

    useEffect(() => {
        forceUpdate();
    }, [props.currentFolder.expiresDays]);

    const handleFolderSelection = (folders: IFolderModel[]) => {
        const isOwnerFolders = folders?.every(folder => {
            if (mailboxUserData && mailboxUserData?.userId === folder.createdBy) {
                return true;
            }
            if (folder.createdBy === user?.userId) {
                return true;
            }
        });

        if (folders?.length === folderData.folders.length) {
            const showMoveToFolders = folders.some(folder => folder.parentId !== rootFolder.folderId);
            const res = isOwnerFolders && showMoveToFolders;

            setIsMoveToFolderEnabled(res);
        } else {
            setIsMoveToFolderEnabled(isOwnerFolders);
        }
        folders = folders.map(folder => {
            folder = { ...folder, path: currentFolderPath + "/" + folder.folderName };
            return folder;
        });
        setSelectedFolders(folders);
        resetToolbar(true);
    };

    const handleFolderClick = (
        folder: IFolderModel,
        sortOptions: { sortColumn: FolderColumnName; sortOrder: SortOrder },
        isTitleBreadcrumb: boolean,
        breadCrumbArrayArchiveTable: IFolderModel[]
    ) => {
        dispatch(fetchSubFolders(folder.folderId, sortOptions.sortColumn, sortOptions.sortOrder));
        props.handleFolderClick(folder, isTitleBreadcrumb, breadCrumbArrayArchiveTable);
    };

    const onRequestFolders = (sortOptions: { sortColumn: FolderColumnName; sortOrder: SortOrder }) => {
        if (folderData && folderData.folders.length > 0) {
            dispatch(fetchSubFolders(folderData.parentFolderId, sortOptions.sortColumn, sortOptions.sortOrder));
        }
    };

    const handleOnClickMoveToFolder = () => {
        // store selected folder as moving folder for Move to folder dialog
        dispatch({ type: actionTypes.ADD_MOVING_FOLDER, payload: selectedFolders });

        // URL format: .../move-to/folderType/isMovingFolder/isDocumentRequest
        const url = `${window.location.origin}/move-to/${FolderType.ARCHIVE}/true/false`;
        Office.context.ui.displayDialogAsync(url, { height: 60, width: 40, displayInIframe: true }, asyncResult => {
            const dialog = asyncResult.value;
            dialog.addEventHandler(
                Office.EventType.DialogMessageReceived,
                (arg: { message: string; origin: string }) => {
                    dialog.close();
                    if (arg.message && arg.message !== "cancel" && arg.message !== "close") {
                        AppNotifier.Success(
                            selectedFolders.length > 1
                                ? ToasterMessages.SUCCESS.MOVE_FOLDERS_TO_FOLDER.replace(
                                    "<destination folder>",
                                    arg.message
                                )
                                : ToasterMessages.SUCCESS.MOVE_FOLDER_TO_FOLDER.replace(
                                    "<destination folder>",
                                    arg.message
                                )
                        );
                    }

                    dispatch({ type: actionTypes.REMOVE_MOVING_FOLDER, payload: [] });
                    props.setReload(true);
                    resetToolbar(true);
                    handleFolderSelection([]);
                }
            );
            dialog.addEventHandler(Office.EventType.DialogEventReceived, () => {
                dispatch({ type: actionTypes.REMOVE_MOVING_FOLDER, payload: [] });
                dialog.close();
            });
        });
    };
    return (
        <LoadingOverlay style={{ innerHeight: "400px", outerHeight: "500px" }}>
            <div className="message-explorer-container">
                <FolderToolbar
                    selectedFolders={selectedFolders}
                    retentionOptions={getRetentionOptions(props.retentionPeriods)}
                    isAnySelected={selectedFolders.length > 0}
                    isMoveToFolderEnabled={isMoveToFolderEnabled}
                    reset={isToolbarReset}
                    showRenameOption={false}
                    setReload={props.setReload}
                    resetSelection={() => handleFolderSelection([])}
                    currentFolder={props.currentFolder}
                    openMoveToFolder={handleOnClickMoveToFolder}
                    isSearched={props.isSearched}
                />

                <div className="message-explorer-body">
                    <ArchiveFolderTable
                        folderData={folderData?.folders ?? []}
                        selectedFolders={selectedFolders}
                        updateFolderSelection={handleFolderSelection}
                        isContentLoading={props.isMessageLoading}
                        requestFolders={onRequestFolders}
                        handleFolderClick={handleFolderClick}
                        retentionPeriods={props.retentionPeriods}
                        setReload={props.setReload}
                        goToParentFolder={props.goToParentFolder}
                        onHide={props.onHide}
                        goToFolder={props.goToFolder}
                        currentFolder={props.currentFolder}
                    />
                </div>
            </div>
            <Loader loading={folderData?.isLoading} />
        </LoadingOverlay>
    );
};
export default MessageExplorer;
